import * as Mutations from './mutations';
import { USER_TYPES } from '../../../constants/user-types';
import { UsersService } from '../../../common/services/settings/users.service';
import { SchoolService } from '../../../common/services/settings/school.service';
import { LoginService } from '../../../common/services/login.service';
import { SchoolSettingsService } from '../../../common/services/settings/school-settings.service';
import { UserData } from '../../../common/auth/user-data';
import * as Roles from '@/constants/user-types';

export const DEFAULT_USER_LOGO = require('@/assets/img/default-user-logo.svg');
export const DEFAULT_SCHOOL_LOGO = require('@/assets/img/Default_School_Logo.jpg');

export const ProfileModule = {
    state: {
        user: {
            firstName: null,
            lastName: null,
            logo: null,
            phone: null,
            email: null,
            logoLoading: false,
        },
        school: {
            city: null,
            contactName: null,
            contactPhone: null,
            country: null,
            currency: null,
            dateFormat: null,
            logo: null,
            postcode: null,
            province: null,
            schoolName: null,
            schoolPhone: null,
            streetName: null,
            tagLine: null,
            timezone: null,
            website: null,
            logoLoading: false,
            features: {
                ['new-payments']: false
            }
        },
        parentPortalUrl: null,
        trial: {
            isTrialEnded: false,
            trialEndDate: null
        }
    },

    mutations: {
        [Mutations.SET_USER_INFO](state, payload) {
            payload.logo = payload.logo || DEFAULT_USER_LOGO;
            state.user = {
                ...state.user,
                ...payload
            }
        },

        [Mutations.SET_SCHOOL_INFO](state, payload) {
            state.school = {
                ...state.school,
                ...payload
            }
        },

        [Mutations.SET_PARENT_PORTAL_URL](state, portalUrl) {
            state.parentPortalUrl = portalUrl;
        },

        [Mutations.SET_TRIAL_PERIOD_INFO](state, trialInfo) {
            state.trial = trialInfo;
        },

        [Mutations.SET_USER_LOGO_REQUEST](state) {
            state.user.logoLoading = true;
        },

        [Mutations.SET_USER_LOGO_SUCCESS](state, logo) {
            state.user.logo = logo || DEFAULT_USER_LOGO;
            state.user.logoLoading = false;
        },

        [Mutations.SET_USER_LOGO_FAILED](state) {
            state.user.logoLoading = false;
        },

        [Mutations.SET_SCHOOL_LOGO_REQUEST](state) {
            state.school.logoLoading = true;
        },

        [Mutations.SET_SCHOOL_LOGO_SUCCESS](state, logo) {
            state.school.logo = logo || DEFAULT_SCHOOL_LOGO;
            state.school.logoLoading = false;
        },

        [Mutations.SET_SCHOOL_LOGO_FAILED](state) {
            state.school.logoLoading = false;
        }
    },

    actions: {
        async getUserInfo({ commit, getters }) {
            const { schoolId, userId } = getters;
            const { data } = await UsersService.getProfileInfo(schoolId, userId);
            commit(Mutations.SET_USER_INFO, data);
        },

        async getSchoolInfo({ commit, getters }) {
            const { schoolId } = getters;
            const { data } = await SchoolService.getProfileInfo(schoolId);
            commit(Mutations.SET_SCHOOL_INFO, data);
        },

        async getTrialPeriodEndDate({ commit, getters }) {
            const { data } = await SchoolService.getTrialPeriodEndDate(getters.schoolId);
            commit(Mutations.SET_TRIAL_PERIOD_INFO, data);
        },

        async getParentPortalURL({ commit, getters }) {
            const { schoolId } = getters;
            const { data } = await LoginService.getParentPortalURL(schoolId);
            commit(Mutations.SET_PARENT_PORTAL_URL, data);
        },

        async updateParentPortalURL({ dispatch, getters }, portalName) {
            const { schoolId } = getters;
            try {
                dispatch('showSpinner');
                await SchoolSettingsService.updatePortalName(schoolId, portalName);
                await dispatch('getParentPortalURL');
                dispatch('createSuccessNotification', 'You have changed your portal name');
            } finally {
                dispatch('hideSpinner');
            }
        },

        async editSchoolProfileInfo({ dispatch, getters }, form) {
            const { schoolId } = getters;
            try {
                dispatch('showSpinner');
                await SchoolService.editProfileInfo(schoolId, form);
                dispatch('createSuccessNotification', 'You have successfully edited your profile');
                await dispatch('getSchoolInfo');
            } finally {
                dispatch('hideSpinner');
            }
        },

        async editGenderOptionInSchoolProfileInfo({ dispatch, getters }, form) {
            const { schoolId } = getters;
            try {
                dispatch('showSpinner');
                await SchoolService.editProfileInfo(schoolId, form);
                await dispatch('getSchoolInfo');
            } finally {
                dispatch('hideSpinner');
            }
        },

        async updateUserLogo({ commit, getters, dispatch }, file) {
            const { schoolId, userId } = getters;
            try {
                commit(Mutations.SET_USER_LOGO_REQUEST);
                const { data } = await UsersService.changeLogo(schoolId, userId, file);
                commit(Mutations.SET_USER_LOGO_SUCCESS, data);
                dispatch('createSuccessNotification', 'You have successfully changed your logo');
            } catch (e) {
                commit(Mutations.SET_USER_LOGO_FAILED);
                if (e.response.status < 500) {
                    dispatch('createErrorNotification', 'Failed to upload an image');
                }
            }
        },

        async removeUserLogo({ commit, dispatch, getters }) {
            const { schoolId, userId } = getters;
            try {
                commit(Mutations.SET_USER_LOGO_REQUEST);
                await UsersService.removeLogo(schoolId, userId);
                commit(Mutations.SET_USER_LOGO_SUCCESS, null);
                dispatch('createSuccessNotification', 'You have removed your logo');
            } catch (e) {
                commit(Mutations.SET_USER_LOGO_FAILED, null);
            }
        },

        async updateSchoolLogo({ commit, dispatch, getters }, file) {
            const { schoolId } = getters;
            try {
                commit(Mutations.SET_SCHOOL_LOGO_REQUEST);
                const { data } = await SchoolService.updateLogo(schoolId, file);
                commit(Mutations.SET_SCHOOL_LOGO_SUCCESS, data);
                dispatch('createSuccessNotification', 'You have successfully changed your logo');
            } catch (e) {
                commit(Mutations.SET_SCHOOL_LOGO_FAILED);
                if (e.response.status < 500) {
                    dispatch('createErrorNotification', 'Failed to upload an image');
                }
            }
        },

        async removeSchoolLogo({ commit, dispatch, getters }) {
            const { schoolId } = getters;
            try {
                commit(Mutations.SET_SCHOOL_LOGO_REQUEST);
                await SchoolService.removeLogo(schoolId);
                commit(Mutations.SET_SCHOOL_LOGO_SUCCESS, null);
                dispatch('createSuccessNotification', 'You have removed your logo');
            } catch (e) {
                commit(Mutations.SET_USER_LOGO_FAILED, null);
            }
        },

        async editUserProfileInfo({ commit, dispatch, getters }, form) {
            const { schoolId, userId } = getters;
            let { firstName, lastName, phone } = form;
            try {
                dispatch('showSpinner');
                await UsersService.editProfileInfo(schoolId, userId, { firstName, lastName, phone });
                dispatch('createSuccessNotification', 'You’ve successfully updated your profile');
                await dispatch('getEnrolledChildrenForActivities');
                const {
                    fullName: name,
                    email,
                    userRole,
                    schoolInfo,
                    dashboardEnrollmentForClasses,
                    dashboardEnrollmentForCamps,
                    dashboardEnrollmentForAftercare
                } = getters;
                const jobTitle = USER_TYPES.find(el => el.data === userRole);
                window.Beacon('identify', {
                    name: name,
                    email: email,
                    company: schoolInfo.schoolName,
                    jobTitle: jobTitle.view,
                    companyName: schoolInfo.schoolName,
                    permissionRole: jobTitle.view,
                    customerSince: schoolInfo.createdAt.slice(0, 10),
                    website: schoolInfo.website,
                    cityState: schoolInfo.city + ', ' + schoolInfo.province,
                    ContactPhone: schoolInfo.schoolPhone,
                    timeZone: schoolInfo.timezone,
                    activeChildren: dashboardEnrollmentForClasses + dashboardEnrollmentForCamps + dashboardEnrollmentForAftercare,
                });
                await dispatch('getUserInfo');
            } finally {
                dispatch('hideSpinner');
            }
        },

        async getUserData({ dispatch }) {
            const getProfileInfo = () => {
                return Promise.all([
                    dispatch('getSchoolInfo'),
                    dispatch('getUserInfo')
                ]);
            };

            const userData = UserData.getUserDataFromLocalStorage();
            const hasTransactionsAccess = [Roles.SCHOOL_ADMIN, Roles.SCHOOL_DIRECTOR];
            

            const promises = [
                getProfileInfo().then(() => dispatch('HelpScoutInit')),
                dispatch('getTrialPeriodEndDate'),
                dispatch('getParentPortalURL'),
            ];

            await Promise.all(promises);
        },

        async HelpScoutInit(context) {
            await context.dispatch('getEnrolledChildrenForActivities');
            window.Beacon('init', process.env.HELP_SCOUT_ID);
            const {
                fullName: name,
                email,
                userRole,
                schoolInfo,
                dashboardEnrollmentForClasses,
                dashboardEnrollmentForCamps,
                dashboardEnrollmentForAftercare
            } = context.getters;
            const jobTitle = USER_TYPES.find(el => el.data === userRole);
            window.Beacon('identify', {
                name: name,
                email: email,
                company: schoolInfo.schoolName,
                jobTitle: jobTitle.view,
                companyName: schoolInfo.schoolName,
                permissionRole: jobTitle.view,
                customerSince: schoolInfo.createdAt.slice(0, 10),
                website: schoolInfo.website,
                cityState: schoolInfo.city + ', ' + schoolInfo.province,
                contactPhone: schoolInfo.schoolPhone,
                timeZone: schoolInfo.timezone,
                activeChildren: dashboardEnrollmentForClasses + dashboardEnrollmentForCamps + dashboardEnrollmentForAftercare,
            });
            window.Beacon('config', {
                messaging: {
                    chatEnabled: true,
                },
            });
        },
    },

    getters: {
        userInfo: state => state.user,

        firstName: state => state.user.firstName,

        lastName: state => state.user.lastName,

        fullName: (state, getters) => getters.firstName + ' ' + getters.lastName,

        phone: state => state.user.phone,

        email: state => state.user.email,

        userLogo: state => state.user.logo,

        userLogoLoading: state => state.user.logoLoading,

        schoolInfo: state => state.school,

        schoolName: state => state.school.schoolName,

        portalName: state => state.school.portalName,

        schoolWebsite: state => state.school.website,

        schoolCountry: state => state.school.country,

        schoolLogo: state => state.school.logo,

        schoolLogoLoading: state => state.school.logoLoading,

        dateFormat: state => state.school.dateFormat || 'mm/dd/yyyy',

        dateFormatYearShort: state => state.school.dateFormat &&
            state.school.dateFormat.slice(0, state.school.dateFormat.length - 2),

        currency: state => state.school.currency,

        currencySign: state => state.school.currency && state.school.currency[state.school.currency.length - 2],

        timezone: state => state.school.timezone,

        parentPortalUrl: state => state.parentPortalUrl,

        trial: state => state.trial,

        isNewPaymentFeatureEnabled: state => {
            if (state.school && state.school.features && state.school.features['new-payments']) {
                return state.school.features['new-payments'];
            }
            return false;
        }
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/profile/profile.module.js